import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, CreditCard, Globe, Banknote, Wallet,
  Building2, Users, ScrollText, LogOut, Menu, ChevronDown, Settings, UserCheck,
  Target, Boxes, HandCoins,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDateRange } from "@/context/DateRangeContext";
import { monthRange, lastMonthRange, yearRange, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROLE_LABELS } from "@/lib/format";

const NAV = [
  { section: "Sales Performance", items: [
    { to: "/", label: "Overview", icon: TrendingUp, testid: "nav-performance", permission: "page.overview" },
    { to: "/sales/credit", label: "Credit Sales", icon: CreditCard, testid: "nav-credit", permission: "page.credit_sales" },
    { to: "/sales/online", label: "Online Sales", icon: Globe, testid: "nav-online", permission: "page.online_sales" },
    { to: "/sales/cash", label: "Cash Sales", icon: Banknote, testid: "nav-cash", permission: "page.cash_sales" },
  ]},
  { section: "Operations", items: [
    { to: "/pettycash", label: "Petty Cash", icon: Wallet, testid: "nav-pettycash", permission: "page.petty_cash" },
    { to: "/company", label: "Company Dashboard", icon: LayoutDashboard, testid: "nav-company", permission: "page.company_dashboard" },
    { to: "/attendance", label: "Staff Attendance", icon: UserCheck, testid: "nav-attendance", permission: "page.attendance" },
    { to: "/kpi", label: "KPI Management", icon: Target, testid: "nav-kpi", permission: "page.kpi" },
    { to: "/stock", label: "Stock Control", icon: Boxes, testid: "nav-stock", permission: "page.stock_control" },
    { to: "/payments", label: "Payment Tracking", icon: HandCoins, testid: "nav-payments", permission: "page.payment_tracking" },
  ]},
];

const MANAGE = { section: "Management", items: [
  { to: "/settings/branches", label: "Branch Master", icon: Building2, testid: "nav-branches", permission: "page.branch_master" },
  { to: "/settings/users", label: "Users & Access", icon: Users, testid: "nav-users", permission: "page.users_access" },
  { to: "/settings/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit", permission: "page.audit_log" },
]};

function NavItems({ onNavigate }) {
  const { can } = useAuth();
  const groups = [...NAV, MANAGE];
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
      {groups.map((g) => (
        <div key={g.section}>
          <div className="px-2 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{g.section}</div>
          <div className="space-y-0.5">
            {g.items.filter((i) => can(i.permission)).map((i) => {
              const Icon = i.icon;
              return (
                <NavLink key={i.to} to={i.to} end={i.to === "/"} onClick={onNavigate} data-testid={i.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                      isActive ? "bg-blue-600 text-white font-semibold" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}>
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                  {i.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SidebarInner({ onNavigate }) {
  return (
    <div className="flex flex-col h-full bg-slate-900">
      <div className="px-5 py-5 border-b border-slate-800">
        <div className="text-white font-display font-black text-xl tracking-tight">PLSC</div>
        <div className="text-slate-400 text-[11px] tracking-wide">Management Operating System</div>
      </div>
      <NavItems onNavigate={onNavigate} />
    </div>
  );
}

function DateRangeBar() {
  const { range, setRange } = useDateRange();
  const presets = [
    { label: "This Month", fn: () => monthRange() },
    { label: "Last Month", fn: () => lastMonthRange() },
    { label: "This Year", fn: () => yearRange() },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="date-preset-btn" className="gap-1">
            {fmtDate(range.start)} – {fmtDate(range.end)} <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 p-3 space-y-2">
          <div className="flex gap-1 flex-wrap">
            {presets.map((p) => (
              <Button key={p.label} size="sm" variant="secondary" className="text-xs"
                data-testid={`preset-${p.label.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setRange(p.fn())}>{p.label}</Button>
            ))}
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Start</label>
            <input type="date" value={range.start} data-testid="date-start-input"
              onChange={(e) => setRange({ ...range, start: e.target.value })}
              className="w-full border border-slate-300 rounded-sm px-2 py-1 text-sm" />
            <label className="text-xs text-slate-500">End</label>
            <input type="date" value={range.end} data-testid="date-end-input"
              onChange={(e) => setRange({ ...range, end: e.target.value })}
              className="w-full border border-slate-300 rounded-sm px-2 py-1 text-sm" />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex w-64 flex-shrink-0 sticky top-0 h-screen">
        <SidebarInner />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 border-0">
                <SidebarInner onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <DateRangeBar />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                <div className="h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                  {(user?.name || "U").slice(0, 1)}
                </div>
                <span className="hidden sm:block text-sm">
                  <span className="font-semibold text-slate-800">{user?.name}</span>
                  <span className="block text-[11px] text-slate-400 -mt-0.5">{ROLE_LABELS[user?.role] || user?.role}</span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/settings/branches")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 md:p-6 max-w-[1600px] w-full">{children}</main>
      </div>
    </div>
  );
}
